import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../pages/landing.css";
import "../pages/blog.css";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = "https://arthataxes.com";

function setSeoTags({ title, description, canonical, ogImage }) {
  const upsert = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", content);
  };
  const upsertLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
    el.setAttribute("href", href);
  };
  const prevTitle = document.title;
  document.title = title;
  upsert("name", "description", description);
  if (canonical) upsertLink("canonical", canonical);
  upsert("property", "og:title", title);
  upsert("property", "og:description", description);
  if (canonical) upsert("property", "og:url", canonical);
  if (ogImage) {
    upsert("property", "og:image", ogImage);
    upsert("name", "twitter:image", ogImage);
  }
  upsert("name", "twitter:card", "summary_large_image");
  upsert("name", "twitter:title", title);
  upsert("name", "twitter:description", description);
  return () => { document.title = prevTitle; };
}

function BlogNav() {
  return (
    <header className="al-nav">
      <div className="al-container">
        <div className="al-nav-inner">
          <Link to="/" className="al-nav-logo" aria-label="Artha Business Solutions — home">
            <img src="/artha-logo.png" alt="Artha" className="al-nav-logo-mark-img" />
            <div className="al-nav-brand">
              Artha Business Solutions LLC
              <small>Tax Advisory &amp; Preparation</small>
            </div>
          </Link>
          <ul className="al-nav-links">
            <li><Link to="/#services">Services</Link></li>
            <li><Link to="/blog">Blog</Link></li>
            <li><Link to="/deadlines">Deadlines</Link></li>
            <li><Link to="/#contact">Contact</Link></li>
          </ul>
          <div className="al-nav-actions">
            <Link to="/login" className="al-btn al-btn-secondary">Client Portal</Link>
            <Link to="/#contact" className="al-btn al-btn-primary">Schedule a Call</Link>
          </div>
        </div>
      </div>
    </header>
  );
}

function BlogFooter() {
  return (
    <footer className="al-footer" role="contentinfo">
      <div className="al-container">
        <p className="al-footer-copy" style={{ textAlign: "center", padding: "40px 0" }}>
          © {new Date().getFullYear()} Artha Business Solutions LLC · Frisco, TX · <a href="tel:4698153491">(469) 815-3491</a>
        </p>
      </div>
    </footer>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
}

export function BlogList() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    const restore = setSeoTags({
      title: "Blog — Artha Insights | Tax, Finance & AI Updates from Frisco, TX",
      description: "Actionable tax strategy, international compliance, and AI-in-finance briefings from the licensed CPAs at Artha Business Solutions LLC.",
      canonical: `${SITE_URL}/blog`,
      ogImage: `${SITE_URL}/og/default.png`,
    });
    fetch(`${API}/api/blog`).then((r) => r.json()).then(setPosts).catch(() => setPosts([]));
    return restore;
  }, []);
  return (
    <div className="al-shell" data-testid="blog-list-page">
      <BlogNav />
      <main>
        <section className="bl-hero">
          <div className="al-container">
            <span className="al-eyebrow">Artha Insights</span>
            <h1>Tax, finance & AI briefings for founders and investors</h1>
            <p>Short, actionable posts on the changes that matter — from a firm that actually files, plans, and defends returns every day.</p>
          </div>
        </section>
        <section className="bl-body">
          <div className="al-container">
            {posts === null && <p className="bl-empty">Loading…</p>}
            {posts && posts.length === 0 && <p className="bl-empty" data-testid="blog-empty">No posts yet — check back soon.</p>}
            {posts && posts.length > 0 && (
              <div className="bl-grid" data-testid="blog-grid">
                {posts.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="bl-card" data-testid={`blog-card-${p.slug}`}>
                    {p.hero_image_url ? (
                      <div className="bl-card-img" style={{ backgroundImage: `url(${p.hero_image_url})` }} aria-hidden="true" />
                    ) : (
                      <div className="bl-card-img bl-card-img--placeholder" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="14" y2="17" /></svg>
                      </div>
                    )}
                    <div className="bl-card-body">
                      <p className="bl-card-date">{fmtDate(p.published_at)}</p>
                      <h2>{p.title}</h2>
                      <p className="bl-card-excerpt">{p.excerpt}</p>
                      {p.tags && p.tags.length > 0 && (
                        <div className="bl-card-tags">
                          {p.tags.slice(0, 3).map((t) => (<span key={t} className="bl-card-tag">{t}</span>))}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <BlogFooter />
    </div>
  );
}

export function BlogPost({ slug }) {
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const [ReactMarkdown, setReactMarkdown] = useState(null);
  useEffect(() => {
    import("react-markdown").then((mod) => setReactMarkdown(() => mod.default));
  }, []);
  useEffect(() => {
    setPost(null); setError(false);
    fetch(`${API}/api/blog/${slug}`).then((r) => {
      if (!r.ok) throw new Error("not found");
      return r.json();
    }).then(setPost).catch(() => setError(true));
    window.scrollTo({ top: 0 });
  }, [slug]);
  useEffect(() => {
    if (!post) return;
    const canonical = `${SITE_URL}/blog/${post.slug}`;
    const restore = setSeoTags({
      title: post.seo_title || `${post.title} — Artha Insights`,
      description: post.seo_description || post.excerpt || "",
      canonical,
      ogImage: post.hero_image_url || `${SITE_URL}/og/default.png`,
    });
    // JSON-LD Article + BreadcrumbList
    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id": `${canonical}#article`,
          headline: post.title,
          description: post.seo_description || post.excerpt || "",
          image: post.hero_image_url || `${SITE_URL}/og/default.png`,
          url: canonical,
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          author: { "@type": "Organization", name: post.author_name || "Artha Insights" },
          publisher: {
            "@type": "Organization",
            name: "Artha Business Solutions LLC",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/logo192.png` },
          },
          mainEntityOfPage: canonical,
          keywords: (post.tags || []).join(", "),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: canonical },
          ],
        },
      ],
    };
    let ld = document.getElementById("blog-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "blog-jsonld"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify(graph);
    return () => { restore(); const el = document.getElementById("blog-jsonld"); if (el) el.remove(); };
  }, [post]);
  if (error) {
    return (
      <div className="al-shell" data-testid="blog-post-not-found">
        <BlogNav />
        <main><div className="al-container" style={{ padding: "80px 0", textAlign: "center" }}>
          <h1>Post not found</h1>
          <p><Link to="/blog">← Back to all posts</Link></p>
        </div></main>
        <BlogFooter />
      </div>
    );
  }
  if (!post) return null;
  return (
    <div className="al-shell" data-testid={`blog-post-${post.slug}`}>
      <BlogNav />
      <main>
        <article className="bl-post">
          <div className="al-container">
            <Link to="/blog" className="bl-back" data-testid="blog-post-back">← All posts</Link>
            <header className="bl-post-head">
              <p className="bl-post-date">{fmtDate(post.published_at)} · {post.author_name || "Artha Insights"}</p>
              <h1>{post.title}</h1>
              {post.excerpt && <p className="bl-post-excerpt">{post.excerpt}</p>}
              {post.tags && post.tags.length > 0 && (
                <div className="bl-post-tags">
                  {post.tags.map((t) => (<span key={t} className="bl-card-tag">{t}</span>))}
                </div>
              )}
            </header>
            {post.hero_image_url && (
              <div className="bl-post-hero"><img src={post.hero_image_url} alt={post.title} /></div>
            )}
            <div className="bl-post-body">
              {ReactMarkdown ? <ReactMarkdown>{post.body_markdown || ""}</ReactMarkdown> : <p>{post.body_markdown}</p>}
            </div>
            <div className="bl-post-cta">
              <p>Have a question about anything in this post?</p>
              <Link to="/#contact" className="al-btn al-btn-primary" data-testid="blog-post-cta">Talk to a CPA →</Link>
            </div>
          </div>
        </article>
      </main>
      <BlogFooter />
    </div>
  );
}

function jurisdictionLabel(j) {
  return ({ federal: "Federal", state: "State", international: "International", firm: "Firm" })[j] || j;
}
function daysUntil(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const now = new Date();
  const diff = Math.round((d - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return `Passed ${-diff}d ago`;
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff <= 30) return `In ${diff} days`;
  const months = Math.round(diff / 30);
  return `In ~${months} month${months > 1 ? "s" : ""}`;
}

export function DeadlinesList() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const restore = setSeoTags({
      title: "Tax & Compliance Deadlines — Federal, State & International | Artha",
      description: "Upcoming federal, state, and international tax and compliance deadlines curated by the licensed CPAs at Artha Business Solutions LLC in Frisco, TX.",
      canonical: `${SITE_URL}/deadlines`,
      ogImage: `${SITE_URL}/og/default.png`,
    });
    fetch(`${API}/api/deadlines`).then((r) => r.json()).then(setItems).catch(() => setItems([]));
    return restore;
  }, []);
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (items || []).filter((d) => d.date >= today);
  const past = (items || []).filter((d) => d.date < today);
  return (
    <div className="al-shell" data-testid="deadlines-page">
      <BlogNav />
      <main>
        <section className="bl-hero">
          <div className="al-container">
            <span className="al-eyebrow">Compliance Calendar</span>
            <h1>Every tax & compliance deadline that matters</h1>
            <p>Federal, multi-state, and international filings — refreshed by our CPAs. Add reminders to your calendar so nothing slips.</p>
          </div>
        </section>
        <section className="bl-body">
          <div className="al-container">
            {items === null && <p className="bl-empty">Loading…</p>}
            {upcoming.length > 0 && (
              <>
                <h2 className="dl-section-title">Upcoming</h2>
                <div className="dl-grid" data-testid="deadlines-upcoming">
                  {upcoming.map((d) => (
                    <div key={d.slug} className={`dl-card dl-card--${d.jurisdiction}`} data-testid={`deadline-card-${d.slug}`}>
                      <div className="dl-card-when">
                        <span className="dl-card-date">{fmtDate(d.date)}</span>
                        <span className="dl-card-in">{daysUntil(d.date)}</span>
                      </div>
                      <div className="dl-card-body">
                        <div className="dl-card-tags">
                          <span className={`dl-tag dl-tag--${d.jurisdiction}`}>{jurisdictionLabel(d.jurisdiction)}</span>
                          {d.audience && d.audience !== "all" && <span className="dl-tag">{d.audience}</span>}
                        </div>
                        <h3>{d.title}</h3>
                        {d.summary && <p>{d.summary}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {past.length > 0 && (
              <>
                <h2 className="dl-section-title" style={{ marginTop: 56, opacity: 0.75 }}>Recently passed</h2>
                <div className="dl-grid dl-grid--past" data-testid="deadlines-past">
                  {past.slice(0, 6).map((d) => (
                    <div key={d.slug} className={`dl-card dl-card--${d.jurisdiction} is-past`} data-testid={`deadline-past-${d.slug}`}>
                      <div className="dl-card-when">
                        <span className="dl-card-date">{fmtDate(d.date)}</span>
                        <span className="dl-card-in">{daysUntil(d.date)}</span>
                      </div>
                      <div className="dl-card-body">
                        <div className="dl-card-tags">
                          <span className={`dl-tag dl-tag--${d.jurisdiction}`}>{jurisdictionLabel(d.jurisdiction)}</span>
                        </div>
                        <h3>{d.title}</h3>
                        {d.summary && <p>{d.summary}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {items && items.length === 0 && <p className="bl-empty">No deadlines listed yet.</p>}
          </div>
        </section>
      </main>
      <BlogFooter />
    </div>
  );
}
